import * as React from 'react';
import {transparentize} from 'polished';
import styled from 'styled-components';
import IconBox from './IconBox';
import {
  IconCheck,
  IconLesson,
  IconTaskCount,
  IconVideoCamera
} from 'icons';
import {LessonObject} from 'api/lesson/types';
import {getImageByPreview} from 'api/library';
import {WebinarObject} from "api/webinar/types";
import {ChapterContentType} from "api/course";
import {navigateTo, openInNewTab} from "helpers/navigate-to";

export interface ScheduleItemContentProps {
  opened?: boolean;
  content: LessonObject | WebinarObject;
  type: ChapterContentType;
}

const Content = styled.div`
  display: flex;
  border-radius: 5px;
  background-color: ${({theme}) => `
      ${transparentize(.5, theme.variables.accentBackgroundColor)};
    `};
`;

export const Container = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 16px;
  margin-left: 42px;

  @media (max-width: 640px) {
    margin-left: 21px;
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const ImageBox = styled.div`
  height: 88px;
  width: 158px;
  min-width: 158px;
  border-radius: 5px;
  overflow: hidden;
  
  @media (max-width: 640px) {
    width: 80px;
    min-width: 80px;
    height: 46px;
    min-height: 46px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
`;

export const Icon = styled.div`
  width: 16px;
  height: 16px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.colors.white)}
  `};
  
  svg {
    width: 100%;
  }
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({theme}) => theme.variables.color};
  cursor: pointer;
  transition: color .22s;

  &:hover {
    color: ${({theme}) => theme.colors.primary};
  }
  
  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

const Tags = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

const Tag = styled.div`
  height: max-content;
  padding: 2px 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  border-radius: 5px;
  background: ${({theme}) => `
    ${transparentize(.85, theme.variables.color)};
  `};
  
  &:not(:first-child) {
    margin-left: 4px;
  }
`;

const Status = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  margin-right: 10px;
  height: 100%;
`;

/**
 * This component renders course schedule item content
 * @param {ScheduleItemContentProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function ScheduleItemContent(props: ScheduleItemContentProps)
  : React.ReactElement {
  /**
   * the function render image box
   * @return {React.ReactElement}
   * @constructor
   */
  function renderImage() {
    const imgSrc = getImageByPreview(props.content.preview);
    return (
      <ImageBox>
        <Image src={imgSrc} />
      </ImageBox>
    );
  }

  /**
   * the function render tags
   * @return {React.ReactNodeArray}
   * @constructor
   */
  function renderTags() {
    if ('tags' in props.content) {
      const tags = props.content?.tags;
      if (tags?.length) {
        return (
          <Tags>
            {tags?.map((tag, index) => (
              <Tag key={index+tag}>{tag}</Tag>)
            )}
          </Tags>
        );
      }
    }
  }

  /**
   * the function render title
   * @return {React.ReactNodeArray}
   * @constructor
   */
  function renderTitle() {
    return (
      <Title onClick={navigateToItemContent}>
        {props.content.title}
      </Title>
    );
  }

  function navigateToItemContent() {
    switch (props.type) {
      case ChapterContentType.Lesson:
        navigateTo('lessons.lesson', {id: props.content.id})
        break;
      case ChapterContentType.Webinar:
        if ('link' in props.content) {
          openInNewTab(props.content.link)
        }
        break;
    }
  }

  /**
   * the function render title
   * @return {React.ReactNodeArray}
   * @constructor
   */
  function renderContentIcon() {
    let icon = null;

    switch (props.type) {
      case ChapterContentType.Lesson:
        icon = <IconLesson />;
        break;
      case ChapterContentType.Webinar:
        icon = <IconVideoCamera />;
        break;
      case ChapterContentType.Task:
        icon = <IconTaskCount />;
        break;
    }

    return (
      <Icon>
        {icon}
      </Icon>
    );
  }

  return (
    <Container>
      {renderContentIcon()}
      <Content>
        {renderImage()}
        <Info>
          {renderTags()}
          {renderTitle()}
        </Info>
        <Status>
          <IconBox
            icon={<IconCheck />}
            size={28}
            shadow
            defaultHidden
            noBorder
          />
        </Status>
      </Content>
    </Container>
  );
}
